import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calendar, Clock, Info } from 'lucide-react';
import { BirthInfo } from '@/lib/ziwei-calculator';

interface BirthInfoFormProps {
  onSubmit: (birthInfo: BirthInfo) => void;
}

const BirthInfoForm: React.FC<BirthInfoFormProps> = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    year: new Date().getFullYear() - 30,
    month: 1,
    day: 1,
    hour: 12,
    minute: 0,
    gender: 'male' as 'male' | 'female',
    isLunar: false,
    hasTime: true
  });

  const [showTimeOptions, setShowTimeOptions] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const birthInfo: BirthInfo = {
      ...formData,
      hour: formData.hasTime ? formData.hour : undefined,
      minute: formData.hasTime ? formData.minute : undefined
    };
    
    onSubmit(birthInfo);
  };

  const handleTimeOptionChange = (hasTime: boolean) => {
    setFormData(prev => ({ ...prev, hasTime }));
    if (!hasTime) {
      setShowTimeOptions(true);
    } else {
      setShowTimeOptions(false);
    }
  };

  // 生成年份選項（1900-2030）
  const yearOptions = [];
  for (let year = 1900; year <= 2030; year++) {
    yearOptions.push(year);
  }

  // 生成月份選項
  const monthOptions = [];
  for (let month = 1; month <= 12; month++) {
    monthOptions.push(month);
  }

  // 生成日期選項
  const dayOptions = [];
  const daysInMonth = new Date(formData.year, formData.month, 0).getDate();
  for (let day = 1; day <= daysInMonth; day++) {
    dayOptions.push(day);
  }

  // 生成時辰選項
  const hourOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    const timeLabel = `${hour.toString().padStart(2, '0')}:00`;
    const chineseHour = getChineseHour(hour);
    hourOptions.push({ value: hour, label: `${timeLabel} (${chineseHour})` });
  }

  function getChineseHour(hour: number): string {
    const chineseHours = [
      '子時', '丑時', '寅時', '卯時', '辰時', '巳時',
      '午時', '未時', '申時', '酉時', '戌時', '亥時'
    ];
    return chineseHours[Math.floor((hour + 1) / 2) % 12];
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          紫微斗數排盤
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 基本信息 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gender">性別</Label>
              <Select 
                value={formData.gender} 
                onValueChange={(value: 'male' | 'female') => 
                  setFormData(prev => ({ ...prev, gender: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">男</SelectItem>
                  <SelectItem value="female">女</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Switch
                  checked={formData.isLunar}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, isLunar: checked }))
                  }
                />
                農曆日期
              </Label>
            </div>
          </div>

          {/* 出生日期 */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">
              出生日期 {formData.isLunar ? '(農曆)' : '(公曆)'}
            </Label>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="year">年</Label>
                <Select 
                  value={formData.year.toString()} 
                  onValueChange={(value) => 
                    setFormData(prev => ({ ...prev, year: parseInt(value) }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map(year => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}年
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="month">月</Label>
                <Select 
                  value={formData.month.toString()} 
                  onValueChange={(value) => 
                    setFormData(prev => ({ ...prev, month: parseInt(value) }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map(month => (
                      <SelectItem key={month} value={month.toString()}>
                        {month}月
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="day">日</Label>
                <Select 
                  value={formData.day.toString()} 
                  onValueChange={(value) => 
                    setFormData(prev => ({ ...prev, day: parseInt(value) }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {dayOptions.map(day => (
                      <SelectItem key={day} value={day.toString()}>
                        {day}日
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* 出生時間選項 */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                出生時間
              </Label>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.hasTime}
                  onCheckedChange={handleTimeOptionChange}
                />
                <span className="text-sm">
                  {formData.hasTime ? '知道確切時間' : '不知道確切時間'}
                </span>
              </div>
            </div>

            {formData.hasTime ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hour">時辰</Label>
                  <Select 
                    value={formData.hour.toString()} 
                    onValueChange={(value) => 
                      setFormData(prev => ({ ...prev, hour: parseInt(value) }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {hourOptions.map(({ value, label }) => (
                        <SelectItem key={value} value={value.toString()}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="minute">分鐘</Label>
                  <Input
                    type="number"
                    min="0"
                    max="59"
                    value={formData.minute}
                    onChange={(e) => 
                      setFormData(prev => ({ ...prev, minute: parseInt(e.target.value) || 0 }))
                    }
                  />
                </div>
              </div>
            ) : (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p>沒有確切出生時間？我們提供以下選項：</p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li><strong>使用午時排盤</strong>：系統將使用中午12點進行計算</li>
                      <li><strong>時辰範圍推算</strong>：可根據大概的時間段進行分析</li>
                      <li><strong>多時辰比較</strong>：可以嘗試不同時辰看哪個更符合性格特徵</li>
                    </ul>
                    <p className="text-sm text-muted-foreground mt-2">
                      注意：沒有確切時間的排盤準確度會受到影響，建議盡量提供準確的出生時間。
                    </p>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>

          <Button type="submit" className="w-full" size="lg">
            開始排盤
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default BirthInfoForm;